import { allowCrop } from '@shared/services/crop';
import type { CartPackageImageNodeDisplayModel } from '@shared/models/carts/cartPackageImageNodes';
import type { Cartv2PackageItemDisplayModel } from '@shared/models/carts/cartPackages';
import type { PackageDisplayModel } from '@shared/models/packages/packages';
import { type SubjectImageDisplayModel, isIndividualSubjectImage } from '@shared/models/subjects/images';
import type { MCropperResult } from '@shared/components/MCropper/models';
import type { ICartPackageStoreItem } from '../stores/cartPackage.cache';

/**
 * RSPO consumer GLOBAL crop (issue #14).
 *
 * The consumer picks at most two "main" images across all packages (one individual portrait + one
 * group photo). Rather than crop every node individually, they crop each source image ONCE in a
 * rectangle cropper; that one framing is then written to every node the image feeds. Because
 * `cropDimensions` lives in source-image space and MLivePreview re-projects it per slot, a single
 * crop trickles correctly to differently-shaped nodes.
 *
 * This module (a) collects the distinct croppable source images actually in play, ordered
 * individuals-first, and (b) writes one cropper result onto every node keyed to a given image.
 */

/**
 * The distinct subject images that (1) are assigned to at least one CROPPABLE node (digital-download
 * items are excluded via `allowCrop`) and (2) resolve to a known subject image. Ordered individuals
 * first, then groups, so the sequential cropper walks portrait -> group. De-duplicated by image id,
 * so the same image shared across many packages is only cropped once.
 */
export function collectGloballyCroppableImages(
  items: ICartPackageStoreItem[],
  isV3: boolean,
): SubjectImageDisplayModel[] {
  const usedImageIds = new Set<number>();

  for (const storeItem of items) {
    const jobPackage = storeItem.jobPackage as PackageDisplayModel | undefined;
    const cartItems = storeItem.cartPackage?.cartPackageItems ?? [];
    for (const cartItem of cartItems) {
      // Skip items that cannot be cropped (e.g. digital downloads).
      if (jobPackage && !allowCrop(jobPackage, cartItem, isV3)) continue;
      for (const node of cartItem.imageNodes ?? []) {
        if (node.subjectImageId != null) usedImageIds.add(node.subjectImageId);
      }
    }
  }

  // Resolve ids -> subject images from any subject that owns them (all packages share one subject).
  const byId = new Map<number, SubjectImageDisplayModel>();
  for (const storeItem of items) {
    for (const image of storeItem.subject?.images ?? []) {
      if (usedImageIds.has(image.id) && !byId.has(image.id)) byId.set(image.id, image);
    }
  }

  const images = [...byId.values()];
  // Individuals first, then groups — the sequence the consumer crops in.
  return images.sort((a, b) => Number(isIndividualSubjectImage(b)) - Number(isIndividualSubjectImage(a)));
}

/** Write a cropper result onto a single node (same field shape as the soft-crop default writer). */
function applyResultToNode(
  node: CartPackageImageNodeDisplayModel,
  result: MCropperResult,
): CartPackageImageNodeDisplayModel {
  return {
    ...node,
    frontendCropDimensions: result.coordinates,
    frontendCropDimensionsStr: JSON.stringify(result.coordinates),
    cropDimensions: result.orderXmlCoordinates,
    rotation: result.image.transforms.rotate ?? 0,
  };
}

/**
 * Apply one global crop result to every node of a cart item that is fed by `subjectImageId`.
 * Nodes for other images are left untouched. Returns a new item (does not mutate the input).
 */
export function applyGlobalCropResultToItem(
  item: Cartv2PackageItemDisplayModel,
  subjectImageId: number,
  result: MCropperResult,
): Cartv2PackageItemDisplayModel {
  const imageNodes = (item.imageNodes ?? []).map((node) =>
    node.subjectImageId === subjectImageId ? applyResultToNode(node, result) : node,
  );
  return { ...item, imageNodes };
}
